import styled from "@emotion/styled";
import Question from "./Question";
import {
  QuestionWithRelations,
  QuestionGroupWithRelations,
} from "../../../../api/src/types";
import { MOBILE_SCREEN } from "../../components/theme";

interface StyledWrapperProps {
  level: number;
}

const StyledQuestionWrapper = styled.div<StyledWrapperProps>`
  padding-left: ${({ level }: { level: number }) => `${24 * level}px`};
  ${MOBILE_SCREEN} {
    padding-left: 0;
  }
`;

interface QuestionTreeRendererProps {
  question: QuestionWithRelations;
  sectionGroupsData: QuestionGroupWithRelations[];
  documentId: string;
  shouldValidateQuestionnaireSection?: boolean;
  isReadOnly?: boolean;
}

interface QueueItem {
  question: QuestionWithRelations;
  level: number;
}

const QuestionTreeRenderer: React.FC<QuestionTreeRendererProps> = ({
  question,
  sectionGroupsData,
  documentId,
  shouldValidateQuestionnaireSection,
  isReadOnly = false,
}) => {
  const renderedQuestions: React.ReactNode[] = [];
  const stack: QueueItem[] = [{ question, level: 0 }];

  while (stack.length > 0) {
    const item = stack.pop(); //LIFO - DFS to preserve the order of parent-child questions
    if (!item) continue;

    const { question, level } = item; //level is used to calculate the padding left for the question to preserve the hierarchy in the UI

    // Render current question
    renderedQuestions.push(
      <StyledQuestionWrapper
        key={question.id}
        data-testid="question-tree-renderer"
        level={level}
      >
        <Question
          question={question}
          documentId={documentId}
          shouldValidateQuestionnaireSection={
            shouldValidateQuestionnaireSection ?? false
          }
          isReadOnly={isReadOnly}
        />
      </StyledQuestionWrapper>,
    );

    // Get selected option from current question's answers
    const selectedOptionId =
      question.answers[0]?.answerQuestionOptions?.[0]?.questionOptionId;

    // Find conditional visibilities that match the current question and its selected option
    const childQuestionIds = question.parentConditionalVisibilities
      .filter(
        (cv) =>
          cv.parentQuestionId === question.id &&
          cv.questionOptionId === selectedOptionId,
      )
      .map((cv) => cv.childQuestionId);

    // Find the group that owns this question
    const parentGroup = sectionGroupsData.find(
      (g) => g.id === question.parentQuestionGroupId,
    );

    // Get all child questions from the same group
    const childQuestions = parentGroup?.questions.filter((q) =>
      childQuestionIds.includes(q.id),
    );

    // Push children in reverse order so they are processed in the original order
    childQuestions
      ?.slice()
      .reverse()
      .forEach((child) => {
        stack.push({ question: child, level: level + 1 });
      });
  }

  return <div>{renderedQuestions}</div>;
};

export default QuestionTreeRenderer;
